'use client'

import { useSearchParams } from 'next/navigation'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { GoogleIntegrationCard } from './google-integration-card'
import { CrmIntegrationCard } from '@/components/crm'
import { ShopifyIntegrationCard } from './shopify-integration-card'
import { DeliveryIntegrationCard } from '@/components/deliveries/delivery-integration-card'
import { MetaAdsIntegrationCard } from '@/components/integrations/meta-ads/meta-ads-integration-card'

export function IntegrationsPage() {
  const searchParams = useSearchParams()
  const { user, isLoading } = useAuth()
  const { can } = usePermissions()
  const role = user?.role
  const isAdmin = role === 'admin'
  const isSuperUser = role === 'super_user'
  const hasAccess = isAdmin || isSuperUser || can(['integrations.*'])

  // Check if user has an active tenant (either own tenant_id or impersonated)
  const hasActiveTenant = Boolean(user?.tenant_id || user?.impersonated_tenant_id)
  const requiresTenantSelection = !hasActiveTenant
  const openDeliveriesByDefault = searchParams.get('section') === 'deliveries'
  const openAdsPlatformsByDefault = searchParams.get('section') === 'ads-platforms'

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-64 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertTitle>Access Restricted</AlertTitle>
            <AlertDescription>
              Only tenant administrators can configure integrations. Please contact an administrator if you need changes.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  if (requiresTenantSelection) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Alert>
            <AlertTitle>Select a tenant</AlertTitle>
            <AlertDescription>
              {isSuperUser
                ? 'Choose a tenant from the switcher in the top navigation before managing integrations.'
                : 'Your user account is missing tenant information. Please contact support.'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
          <p className="text-muted-foreground mt-1">Manage your external service integrations</p>
        </div>
        <GoogleIntegrationCard />
        <CrmIntegrationCard />
        <section id="ads-platforms" className="scroll-mt-6">
          <MetaAdsIntegrationCard
            key={openAdsPlatformsByDefault ? 'ads-platforms-open' : 'ads-platforms-closed'}
            defaultOpen={openAdsPlatformsByDefault}
          />
        </section>
        <section id="delivery-integrations" className="scroll-mt-6">
          <DeliveryIntegrationCard
            key={openDeliveriesByDefault ? 'deliveries-open' : 'deliveries-closed'}
            defaultOpen={openDeliveriesByDefault}
          />
        </section>
        <ShopifyIntegrationCard />
      </div>
    </div>
  )
}
